import React, { useEffect, useState } from "react";
import { Typography, Empty, Button } from "antd";
import { IconShare, IconCopy, IconBrandWhatsapp, IconGift, IconUsers, IconShoppingBag, IconCircleCheck, IconSparkles } from '@tabler/icons-react';
import UserServ from "../service/UserServ";
import SettingsServ from "../service/SettingsServ";
import Breadcrumbs from "../component/Breadcrumbs";
import Notify from "../util/Notify";
import { usePreference } from "../context/PreferenceContext";
import logo from "../assets/img/nearzon.png";
import "../css/BuyerShareEarn.css";

const { Title, Text } = Typography;

const BuyerShareEarn = () => {
    const { t } = usePreference();
    const [profile, setProfile] = useState(null);
    const [rewards, setRewards] = useState({ referrer: 100, invitee: 100, welcome: 50 });
    const [loading, setLoading] = useState(false);

    const fetchData = async () => {
        try {
            setLoading(true);
            const [profileRes, refRes, invRes, welRes] = await Promise.allSettled([
                UserServ.getProfile(),
                SettingsServ.get("referral_bonus_referrer"),
                SettingsServ.get("referral_bonus_invitee"),
                SettingsServ.get("registration_bonus")
            ]);

            if (profileRes.status === 'fulfilled' && profileRes.value.data) {
                setProfile(profileRes.value.data);
            }

            const newRewards = {
                referrer: (refRes.status === 'fulfilled' && refRes.value?.data !== null) ? Number(refRes.value.data) : 100,
                invitee: (invRes.status === 'fulfilled' && invRes.value?.data !== null) ? Number(invRes.value.data) : 100,
                welcome: (welRes.status === 'fulfilled' && welRes.value?.data !== null) ? Number(welRes.value.data) : 50,
            };
            console.log("Fetched Rewards:", newRewards);
            setRewards(newRewards);
        } catch (error) {
            console.error("Failed to load data", error);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchData();
    }, []);

    const getShareMessage = () => {
        const referralCode = profile?.referralCode || "USER";
        return `🔥 Hey! Check this out 👇\n\nI’ve been using *Nearzon* for awesome deals 🛍️\nYou should try it too!\n\n🎁 Use my referral code: *${referralCode}*\nand unlock exclusive benefits\n\n📲 Download now: https://nearzon.com`;
    };

    const handleCopyCode = (text) => {
        navigator.clipboard.writeText(text)
            .then(() => Notify.success("Referral code copied!"))
            .catch(() => Notify.error("Failed to copy"));
    };

    const handleCopyFullMessage = () => {
        navigator.clipboard.writeText(getShareMessage())
            .then(() => Notify.success("Full message copied!"))
            .catch(() => Notify.error("Failed to copy"));
    };

    const handleWhatsAppShare = () => {
        const message = encodeURIComponent(getShareMessage());
        window.open(`https://wa.me/?text=${message}`, '_blank');
    };

    const handleNativeShare = () => {
        if (navigator.share) {
            navigator.share({
                title: 'Nearzon Referral',
                text: getShareMessage(),
            }).catch(console.error);
        } else {
            handleCopyFullMessage();
        }
    };

    const hasReferral = profile && profile.referralCode;

    return (
        <div className="sd-container pcu-max-w-1200 pcu-margin-x-auto pcu-padding-20">
            <div className="pcu-margin-bottom-16">
                <Breadcrumbs />
            </div>

            <div className="sd-header" style={{ marginBottom: '24px', background: 'transparent', padding: 0 }}>
                <div>
                    <h1 style={{ margin: 0, fontSize: '24px', fontWeight: 800, color: '#1a1d2e', letterSpacing: '-0.5px', display: 'inline-flex', alignItems: 'center', gap: 10 }}>
                        <span style={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center', width: 36, height: 36, borderRadius: 10, background: 'linear-gradient(135deg, #fff7ed, #ffedd5)', border: '1px solid rgba(249,115,22,0.25)' }}>
                            <IconSparkles size={20} color="#ea580c" stroke={1.8} />
                        </span>
                        Share and Earn
                    </h1>
                </div>
            </div>

            <div className="share-earn-container">
                {!hasReferral && !loading ? (
                        <div className="share-card pcu-padding-y-60">
                            <Empty
                                image="https://cdn-icons-png.flaticon.com/512/9018/9018889.png"
                                imageStyle={{ height: 100 }}
                                description={
                                    <div>
                                        <Title level={4}>Share with friends to earn points.</Title>
                                        <Text type="secondary">Please check back later.</Text>
                                    </div>
                                }
                            />
                        </div>
                    ) : (
                        <>
                            <div className="share-earn-main-row">
                                {/* Left Side: Referral Hero Card */}
                                <div className="referral-hero-card">
                                    <div className="pcu-flex pcu-justify-center pcu-items-center pcu-gap-12 pcu-margin-bottom-8">
                                        <IconSparkles size={32} color="#fcd34d" />
                                        <h1 className="hero-title">Refer & Earn</h1>
                                    </div>
                                    <div className="reward-badge">
                                        Earn {rewards.referrer} Points
                                    </div>
                                    <p className="hero-subtitle">
                                        Invite your friends! You get {rewards.referrer} points and they get {rewards.invitee} points when they join!
                                    </p>
                                    
                                    <div className="referral-ticket">
                                        <div>
                                            <div className="ticket-label">Your Referral Code</div>
                                            <div className="ticket-code">{profile?.referralCode}</div>
                                        </div>
                                        <Button 
                                            type="primary" 
                                            icon={<IconCopy size={18} />} 
                                            onClick={() => handleCopyCode(profile?.referralCode)}
                                            className="copy-btn-premium"
                                        >
                                            Copy
                                        </Button>
                                    </div>
                                </div>

                                {/* Right Side: Share Options */}
                                <div className="share-side-section">
                                    <h2 className="share-section-title">Share your invite link</h2>
                                    <div className="share-grid-side">
                                        <div className="share-card-side" onClick={handleWhatsAppShare}>
                                            <div className="share-icon-wrapper whatsapp-wrapper">
                                                <IconBrandWhatsapp size={24} />
                                            </div>
                                            <span className="share-card-label">WhatsApp</span>
                                        </div>
                                        <div className="share-card-side" onClick={handleNativeShare}>
                                            <div className="share-icon-wrapper native-wrapper">
                                                <IconShare size={24} />
                                            </div>
                                            <span className="share-card-label">Share link</span>
                                        </div>
                                        <div className="share-card-side" onClick={handleCopyFullMessage}>
                                            <div className="share-icon-wrapper copy-wrapper">
                                                <IconCopy size={24} />
                                            </div>
                                            <span className="share-card-label">Copy message</span>
                                        </div>
                                    </div>
                                </div>
                            </div>

                            {/* How It Works Section */}
                            <div className="how-it-works-section">
                                <h2 className="pcu-text-center pcu-margin-bottom-16" style={{ fontSize: 24, fontWeight: 800, color: '#0f172a' }}>How it works</h2>
                                <p className="pcu-text-center pcu-color-gray-500 pcu-margin-bottom-32" style={{ maxWidth: 400, margin: '0 auto' }}>
                                    Follow these simple steps to start earning amazing rewards
                                </p>
                                <div className="steps-container">
                                    <div className="step-item">
                                        <div className="step-number">
                                            <IconUsers size={24} />
                                        </div>
                                        <p className="step-text">Share your code<br/>with friends</p>
                                    </div>
                                    <div className="step-item">
                                        <div className="step-number">
                                            <IconShoppingBag size={24} />
                                        </div>
                                        <p className="step-text">Friends sign up<br/>and shop</p>
                                    </div>
                                    <div className="step-item">
                                        <div className="step-number">
                                            <IconCircleCheck size={24} />
                                        </div>
                                        <p className="step-text">Both get<br/>rewarded!</p>
                                    </div>
                                </div>
                            </div>
                        </>
                    )}
                </div>
            </div>
    );

};

export default BuyerShareEarn;
